import React from "react";
import PropTypes from "prop-types";

// KpiCard — the standard headline-metric tile shared across pages (Allocation, Approvals, …):
// a tone-coloured status dot + label, the headline figure, an optional context line, an
// optional ⓘ tooltip, and an optional chart node (e.g. a Sparkline) at the bottom. Calm by
// default — the value only takes colour for an exception ("attention" amber); "ok" keeps the
// headline black with a green dot.
//
// Presentational only: any chart is passed in as a `chart` node so this component carries no
// chart dependency and can live in ui-components without importing from a views/ folder.
const DOT = { neutral: "bg-text-3", attention: "bg-warning", ok: "bg-success" };
const HEADLINE = { neutral: "text-text", attention: "text-warning", ok: "text-text" };

export default function KpiCard({ label, value, sub, tone = "neutral", info, chart, className = "" }) {
	return (
		<div className={`rounded-card border border-card-bd bg-card px-4 py-3 ${className}`}>
			<div className="flex items-center gap-1.5">
				<span className={`inline-block h-2 w-2 rounded-full ${DOT[tone] || DOT.neutral}`} />
				<span className="text-xs font-medium uppercase tracking-wide text-text-2">{label}</span>
				{info ? (
					<span
						// text-xs/leading-none so the info glyph doesn't inflate the label row height and
						// push this tile's value out of line with the others'.
						className="ml-auto cursor-help text-xs leading-none text-text-3"
						data-tooltip-id="instantTooltip"
						data-tooltip-content={info}
						aria-label={info}
					>
						ⓘ
					</span>
				) : null}
			</div>
			<div className={`mt-1 text-xl font-semibold tabular-nums ${HEADLINE[tone] || HEADLINE.neutral}`}>{value}</div>
			{sub && <div className="mt-1 text-xs leading-snug text-text-2">{sub}</div>}
			{chart && <div className="mt-2">{chart}</div>}
		</div>
	);
}

KpiCard.propTypes = {
	label: PropTypes.node.isRequired,
	value: PropTypes.node.isRequired,
	sub: PropTypes.node,
	tone: PropTypes.oneOf(["neutral", "attention", "ok"]),
	// Explanatory copy shown via the shared react-tooltip on a small ⓘ glyph.
	info: PropTypes.string,
	// Optional chart node rendered at the bottom of the card (e.g. a <Sparkline/>).
	chart: PropTypes.node,
	// Extra classes merged onto the card root — used to control width/flex at the call site.
	className: PropTypes.string,
};
